// frontend/src/pages/admin/Subscribers.js
//
// The newsletter consent register.
//
// routes/newsletter.js has had GET /admin/subscribers and GET /admin/export.csv
// for several rounds, both admin-guarded, both careful — tokens are never
// selected, the CSV is formula-escaped, the export writes an audit entry. Neither
// was reachable: no page, no link, so the record of who consented and when was
// visible only from psql. That record is the lawful basis for every marketing
// email we might send, which makes "you cannot look at it" the wrong answer.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import AdminSidebar from '../../components/AdminSidebar';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../../i18n';
import { format } from 'date-fns';
import { AlertTriangle, RefreshCw, Download, Info } from 'lucide-react';
import theme from '../../theme';

// The self-hosted copy in public/fonts, referenced once from index.html.
// This used to be a Google Fonts URL injected per page, which disclosed every
// visitor's IP to a third party on every page load and contradicted the privacy
// policy's "no third-party requests" claim. Kept as a constant because several
// pages import it into an @import rule rather than a <link>.
const FONTS = '/fonts/fonts.css';

const PAGE_SIZE = 50;

// Matches the server's STATUS_FILTERS vocabulary exactly — anything else comes
// back as a 400 naming the allowed values.
const FILTERS = ['all', 'confirmed', 'pending', 'unsubscribed'];

const STATUS_COLORS = {
  confirmed:    theme.state.success,
  pending:      theme.state.pending,
  unsubscribed: theme.state.muted,
};

const serverText = (err, fallback) =>
  (err && typeof err === 'object' && err.error) ? (err.message || err.error) : fallback;

export default function AdminSubscribers() {
  const { t } = useTranslation();
  const dateLocale = getDateLocale();

  const [rows, setRows]           = useState([]);
  const [counts, setCounts]       = useState(null);
  const [filter, setFilter]       = useState('all');
  const [page, setPage]           = useState(1);
  const [loading, setLoading]     = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [exporting, setExporting] = useState(false);
  // Two filter changes in flight do not answer in order, and a stale answer
  // landing last would list unsubscribed addresses under 'confirmed' — the one
  // mistake on this screen that turns into an unlawful email.
  const loadSeq = useRef(0);

  useEffect(() => {
    const link = document.createElement('link'); link.rel = 'stylesheet'; link.href = FONTS; document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);

  useEffect(() => { setPage(1); }, [filter]);

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    const seq = ++loadSeq.current;
    const params = new URLSearchParams({ status: filter, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE });
    api.get(`/newsletter/admin/subscribers?${params}`)
      .then(d => {
        if (seq !== loadSeq.current) return;
        setRows(Array.isArray(d?.subscribers) ? d.subscribers : []);
        setCounts(d?.counts || null);
      })
      .catch(err => {
        if (seq !== loadSeq.current) return;
        setLoadError(serverText(err, t('admin.subscribers.loadFailedDetail')));
      })
      .finally(() => { if (seq === loadSeq.current) setLoading(false); });
  }, [filter, page, t]);

  useEffect(() => { load(); }, [load]);

  // The endpoint returns the whole-table breakdown rather than a count for the
  // active filter, so the pager reads its total out of that.
  const filteredCount = counts
    ? (filter === 'all' ? counts.total : Number(counts[filter]) || 0)
    : 0;
  const totalPages = Math.max(1, Math.ceil(filteredCount / PAGE_SIZE));

  const handleExport = async () => {
    setExporting(true);
    try {
      // A plain link would arrive without the bearer token, so the file is
      // fetched and handed to the browser — same as the revenue export.
      const blob = await api.get('/newsletter/admin/export.csv', { responseType: 'blob' });
      const url = URL.createObjectURL(blob instanceof Blob ? blob : new Blob([blob], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `glamgo-newsletter-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(serverText(err, t('admin.subscribers.exportFailed')), { duration: 8000 });
    } finally {
      setExporting(false);
    }
  };

  const when = (v) => (v ? format(new Date(v), 'd MMM yyyy, HH:mm', { locale: dateLocale }) : '—');

  const STATS = [
    { key: 'confirmed', label: t('admin.subscribers.statConfirmed'), color: theme.greenDeep },
    { key: 'pending', label: t('admin.subscribers.statPending'), color: theme.amber },
    { key: 'unsubscribed', label: t('admin.subscribers.statUnsubscribed'), color: theme.grey400 },
  ];

  return (
    <div style={{ minHeight: '100vh', background: theme.grey100, fontFamily: theme.font.body, display: 'flex' }}>
      <AdminSidebar />

      <div className="admin-main" style={{ padding: '40px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16, marginBottom: 28 }}>
          <div>
            <h1 style={{ fontFamily: theme.font.display, fontSize: 40, color: theme.ink, margin: 0 }}>{t('admin.subscribers.title')}</h1>
            <p style={{ color: theme.grey400, margin: '4px 0 0' }}>{t('admin.subscribers.subtitle')}</p>
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {STATS.map(s => (
              <div key={s.key} style={{ background: theme.white, border: `1px solid ${theme.creamBone}`, borderRadius: 10, padding: '14px 22px', minWidth: 110, textAlign: 'center' }}>
                <div style={{ fontFamily: theme.font.display, fontSize: 32, lineHeight: 1, color: s.color }}>
                  {counts ? (Number(counts[s.key]) || 0) : '—'}
                </div>
                <div style={{ fontSize: 11, color: theme.grey400, marginTop: 6, letterSpacing: '0.06em' }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18, alignItems: 'center' }}>
          {FILTERS.map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{
              padding: '8px 18px',
              border: filter === f ? 'none' : `1px solid ${theme.sandMid}`,
              background: filter === f ? theme.ink : 'transparent',
              color: filter === f ? theme.white : theme.grey600,
              borderRadius: 100, fontSize: 11, letterSpacing: '0.06em', cursor: 'pointer', fontFamily: theme.font.body, whiteSpace: 'nowrap',
            }}>
              {t(`admin.subscribers.filter.${f}`)}
            </button>
          ))}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button onClick={load} title={t('admin.subscribers.refresh')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', border: `1px solid ${theme.sand}`, background: theme.white, borderRadius: 4, cursor: 'pointer', fontSize: 12, color: theme.grey600, fontFamily: theme.font.body }}>
              <RefreshCw size={13} /> {t('admin.subscribers.refresh')}
            </button>
            <button onClick={handleExport} disabled={exporting}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 18px', border: 'none', background: theme.ink, color: theme.white, borderRadius: 4, cursor: exporting ? 'wait' : 'pointer', opacity: exporting ? 0.6 : 1, fontSize: 12, letterSpacing: '0.06em', fontFamily: theme.font.body }}>
              <Download size={13} /> {exporting ? t('admin.subscribers.exporting') : t('admin.subscribers.export')}
            </button>
          </div>
        </div>

        {/* The export is confirmed-and-not-unsubscribed only, by design and with
            no parameter to widen it, and it writes an audit entry. Both facts
            belong on the screen rather than only in the route file. */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9, background: theme.white, border: `1px solid ${theme.creamBone}`, borderRadius: 6, padding: '11px 15px', marginBottom: 24, fontSize: 12.5, color: theme.grey600 }}>
          <Info size={15} style={{ flexShrink: 0, marginTop: 1, color: theme.grey400 }} />
          <span>{t('admin.subscribers.exportNote')}</span>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 100, color: theme.gold, fontFamily: theme.font.display, fontSize: 24 }}>{t('admin.subscribers.loading')}</div>
        ) : loadError ? (
          <div style={{ background: theme.white, border: `1px solid ${theme.redWash}`, borderRadius: 10, padding: 48, textAlign: 'center' }}>
            <AlertTriangle size={26} style={{ color: theme.redBrick }} />
            <div style={{ fontFamily: theme.font.display, fontSize: 26, color: theme.ink, margin: '10px 0 6px' }}>{t('admin.subscribers.loadFailedTitle')}</div>
            <p style={{ color: theme.grey400, margin: '0 0 20px', fontSize: 13 }}>{loadError}</p>
            <button onClick={load} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: theme.ink, color: theme.white, border: 'none', padding: '12px 26px', borderRadius: 8, cursor: 'pointer', fontSize: 12, letterSpacing: '0.1em', fontFamily: theme.font.body }}>
              <RefreshCw size={13} /> {t('admin.subscribers.retry')}
            </button>
          </div>
        ) : rows.length === 0 ? (
          <div style={{ background: theme.white, border: `1px solid ${theme.creamBone}`, borderRadius: 10, padding: 72, textAlign: 'center' }}>
            <div style={{ fontFamily: theme.font.display, fontSize: 26, color: theme.grey400 }}>
              {filter === 'all' ? t('admin.subscribers.empty') : t('admin.subscribers.emptyFiltered')}
            </div>
          </div>
        ) : (
          <div style={{ background: theme.white, border: `1px solid ${theme.creamBone}`, borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
                <thead>
                  <tr style={{ background: theme.cream }}>
                    {['colEmail', 'colStatus', 'colSubscribed', 'colConfirmed', 'colUnsubscribed', 'colSource'].map(h => (
                      <th key={h} style={{ padding: '12px 16px', textAlign: 'left', color: theme.grey400, fontWeight: 500, fontSize: 11.5, letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>
                        {t(`admin.subscribers.${h}`)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map(s => {
                    const sc = STATUS_COLORS[s.status] || theme.state.neutral;
                    return (
                      <tr key={s.id} style={{ borderBottom: `1px solid ${theme.creamDeep}` }}>
                        <td style={{ padding: '13px 16px', color: theme.ink, wordBreak: 'break-all' }}>{s.email}</td>
                        <td style={{ padding: '13px 16px', whiteSpace: 'nowrap' }}>
                          <span style={{ background: sc.bg, color: sc.color, padding: '3px 11px', borderRadius: 100, fontSize: 10.5, letterSpacing: '0.05em', fontWeight: 500 }}>
                            {t(`admin.subscribers.status.${s.status}`, { defaultValue: s.status })}
                          </span>
                          {/* An unconfirmed address is not consent. Saying so on
                              the row is cheaper than explaining it after
                              somebody has pasted the list into a mail tool. */}
                          {s.status === 'pending' && (
                            <div style={{ fontSize: 11, color: theme.grey400, marginTop: 4, maxWidth: 240, lineHeight: 1.45 }}>
                              {t('admin.subscribers.pendingHint')}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '13px 16px', color: theme.grey600, whiteSpace: 'nowrap' }}>{when(s.subscribed_at)}</td>
                        <td style={{ padding: '13px 16px', color: theme.grey600, whiteSpace: 'nowrap' }}>{when(s.confirmed_at)}</td>
                        <td style={{ padding: '13px 16px', color: theme.grey600, whiteSpace: 'nowrap' }}>{when(s.unsubscribed_at)}</td>
                        <td style={{ padding: '13px 16px', color: theme.grey400, fontSize: 12 }}>{s.source || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!loading && !loadError && filteredCount > PAGE_SIZE && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 24, fontSize: 13, color: theme.grey500 }}>
            <span>
              {t('admin.subscribers.pagination', {
                from: (page - 1) * PAGE_SIZE + 1,
                to: Math.min(page * PAGE_SIZE, filteredCount),
                total: filteredCount,
              })}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                style={{ padding: '8px 16px', border: `1px solid ${theme.sand}`, borderRadius: 4, background: theme.white, cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.4 : 1, fontFamily: theme.font.body, fontSize: 12 }}>
                {t('admin.subscribers.prev')}
              </button>
              <span style={{ padding: '8px 12px', background: theme.ink, color: theme.white, borderRadius: 4, fontSize: 12 }}>{page} / {totalPages}</span>
              <button onClick={() => setPage(p => p + 1)} disabled={page * PAGE_SIZE >= filteredCount}
                style={{ padding: '8px 16px', border: `1px solid ${theme.sand}`, borderRadius: 4, background: theme.white, cursor: page * PAGE_SIZE >= filteredCount ? 'not-allowed' : 'pointer', opacity: page * PAGE_SIZE >= filteredCount ? 0.4 : 1, fontFamily: theme.font.body, fontSize: 12 }}>
                {t('admin.subscribers.next')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
